import { z } from "zod";

/**
 * Адреса приходят из /config.json в рантайме, а НЕ из VITE_* на этапе сборки.
 *
 * Причина: если зашить URL в бандл, переезд в РФ перестаёт быть механическим,
 * потому что артефакт придётся пересобирать под каждый регион. Здесь один и
 * тот же бандл раскладывается и в EU, и в РФ, отличается только config.json,
 * который генерируется при деплое.
 */
export const RuntimeConfig = z.object({
  region: z.enum(["eu", "ru"]),
  apiBaseUrl: z.string().url(),
  livekitUrl: z.string().url(),
  /**
   * Hocuspocus. Адрес приходит отсюда по той же причине, что и остальные:
   * зашитый в бандл ws-хост означал бы пересборку артефакта под каждый регион.
   */
  collabUrl: z.string().url(),
  /**
   * Показывать ли кнопку «продолжить как гость».
   *
   * Значение приходит из config.json, а не из сборки, по той же причине, что и
   * адреса: один и тот же бандл раскладывается в окружения с разными
   * настройками. Сервер всё равно решает сам (`ALLOW_GUEST_SIGNUP`), здесь
   * только вопрос, рисовать кнопку или нет.
   *
   * `default(false)` не косметика: со старым config.json, где поля ещё нет,
   * приложение обязано вести себя как при закрытой двери. Ошибка в сторону
   * «кнопки нет» это лишний вопрос пользователя, ошибка в другую сторону это
   * кнопка, которая молча отдаёт 404.
   */
  allowGuestSignup: z.boolean().default(false),
  /**
   * Показывать ли кнопку «Войти через Google».
   *
   * `default(false)` по той же причине, что и у гостевого входа, и здесь она
   * даже весомее: сервер регистрирует маршруты входа через Google, только если
   * у него есть все три ключа. Кнопка, ведущая на несуществующий адрес, отдаёт
   * 404 после полной навигации, то есть человек уходит со страницы и попадает
   * в никуда. Такая кнопка хуже отсутствующей.
   */
  googleSignInEnabled: z.boolean().default(false),
  /**
   * Показывать ли кнопку «Текст с картинки» в редакторе заготовки
   * (`docs/handwriting-recognition-design.md`, шаг 3а).
   *
   * ⚠️ **Это выключатель ТОЛЬКО интерфейса**, и защитой он не является. Сервер
   * регистрирует ручку чтения по наличию прокси, а не по этому полю: прямой
   * вызов ручки ответит и при `false`, и расход такого вызова ограничивает
   * квота чтения, а не кнопка. Заведён 06.10.2026 под гейт замера шага 0б;
   * гейт снят решением владельца 07.10.2026, и на стенде кнопка включена
   * (`IMAGE_TEXT_UI` в `deploy-edge.sh`, умолчание `true`). Выключатель
   * остался аварийным, и записанный путь погасить панель один: выкатка
   * `deploy-edge.sh` целиком с `IMAGE_TEXT_UI=false`. Ручная правка
   * `config.json` на edge тоже погасит кнопку, но следующая выкатка соберёт
   * файл заново и правку потеряет.
   *
   * `default(false)`: со старым config.json, где поля нет, панели нет, и это
   * осознанно. Поле отсутствует только у config.json старше самого
   * выключателя, а таких на стенде нет. Ошибка в сторону «кнопки нет» стоит
   * одной недоступной кнопки.
   */
  imageTextEnabled: z.boolean().default(false),
  telemetry: z.object({
    enabled: z.boolean(),
    sampleIntervalMs: z.number().int().positive(),
  }),
  /**
   * Версия сборки, разложенной на сервере прямо сейчас. Ею открытая вкладка
   * сверяет свой впечённый `__BUILD_ID__` и понимает, что устарела
   * (`lib/build-version.ts`).
   *
   * ⚠️ Необязательное: со старым `config.json`, где поля ещё нет, `parse` иначе
   * уронил бы приложение целиком, то есть механизм подсказки про обновление
   * стал бы причиной белого экрана. Отсутствие означает «не знаем», и полосы
   * тогда нет вовсе.
   */
  buildId: z.string().optional(),
});
export type RuntimeConfig = z.infer<typeof RuntimeConfig>;

let cached: RuntimeConfig | null = null;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  const res = await fetch("/config.json", { cache: "no-store" });
  if (!res.ok) throw new Error(`config.json: HTTP ${res.status}`);
  cached = RuntimeConfig.parse(await res.json());
  return cached;
}

/**
 * Версия сборки, лежащей на сервере СЕЙЧАС.
 *
 * ⚠️ Своим запросом и МИМО кэша `cached` выше, хотя читает тот же файл. Причина
 * в том, что `cached` заведён ровно затем, чтобы адреса не менялись посреди
 * жизни вкладки: подложив в него свежий разбор, мы дали бы приложению менять
 * адрес API на живой сессии. Здесь же нужно противоположное, каждый раз новое
 * значение.
 *
 * Отказ проглатывается: сеть могла отвалиться, а полоса «обновите страницу» из
 * оборванного запроса это ложная тревога.
 */
export async function fetchServerBuildId(): Promise<string | undefined> {
  try {
    const res = await fetch("/config.json", { cache: "no-store" });
    if (!res.ok) return undefined;
    const parsed = RuntimeConfig.safeParse(await res.json());
    return parsed.success ? parsed.data.buildId : undefined;
  } catch {
    return undefined;
  }
}
